/**
 * ExpectedReturnsChart — 1Y-horizon expected-returns history per asset.
 *
 * Renders one trace per selected asset at the 1-year horizon (the 3M/3Y
 * horizons are deliberately not shown) and draws a dashed vintage-cursor
 * line marking the currently-selected vintage.
 */
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { Data, Layout } from 'plotly.js';
import PlotlyChart from '@/lib/plotly/PlotlyChart';
import ChartCard from '@/components/ChartCard';
import QueryState from '@/components/QueryState';
import { ASSET_COLORS } from '@/lib/plotly/colors';
import { mergeLayout } from '@/lib/plotly/defaults';
import { hoverTemplate } from '@/lib/plotly/hoverTemplates';
import { getWindowRange, tickConfigForTimeframe } from '@/lib/utils/timeframe';
import { COMPACT_DATE_TICKS, compactChartLayout } from '../lib/compactChart';
import { fetchExpectedReturns, mlKeys } from '../api';
import { vintageCursorAnnotation, vintageCursorShape } from '../lib/vintageCursor';
import type { HorizonMonths, UiAsset } from '../types';
import { ASSET_DISPLAY } from '../types';
import type { Timeframe } from '../store';

const HORIZON: HorizonMonths = 12;

export interface ExpectedReturnsChartProps {
  selectedAssets: ReadonlySet<UiAsset>;
  vintageDate: string;
  timeframe: Timeframe;
}

export default function ExpectedReturnsChart({
  selectedAssets,
  vintageDate,
  timeframe,
}: ExpectedReturnsChartProps): JSX.Element {
  const assets = useMemo(() => [...selectedAssets], [selectedAssets]);

  const queries = useQueries({
    queries: assets.map((asset) => ({
      queryKey: mlKeys.expectedReturns(asset, HORIZON),
      queryFn: () => fetchExpectedReturns(asset, HORIZON),
      staleTime: 5 * 60 * 1000,
    })),
  });

  const isLoading = queries.some((q) => q.isLoading);

  // TanStack Query's `data` references are stable across renders, so we use
  // them (not the fresh `queries` array identity) as memo dependencies.
  const dataRefs = queries.map((q) => q.data);

  const traces: Data[] = useMemo(() => {
    if (!assets.length) return [];
    const [startStr, endStr] = getWindowRange(vintageDate, timeframe);
    const out: Data[] = [];

    assets.forEach((asset, assetIdx) => {
      const data = dataRefs[assetIdx];
      if (!data?.length) return;
      const display = ASSET_DISPLAY[asset];
      const dates: string[] = [];
      const values: number[] = [];
      for (const r of data) {
        if (r.vintage_date >= startStr && r.vintage_date <= endStr) {
          dates.push(r.vintage_date);
          values.push(r.value);
        }
      }
      if (!dates.length) return;
      out.push({
        x: dates,
        y: values,
        type: 'scatter',
        mode: 'lines',
        name: display,
        line: { color: ASSET_COLORS[asset], width: 2 },
        hovertemplate: hoverTemplate({ name: display, suffix: '%' }),
      });
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assets, ...dataRefs, vintageDate, timeframe]);

  const layout: Partial<Layout> = useMemo(() => {
    const [startStr] = getWindowRange(vintageDate, timeframe);
    const endPadded = new Date(vintageDate);
    endPadded.setMonth(endPadded.getMonth() + 1);
    const { tickformat } = tickConfigForTimeframe(timeframe);

    const cursorShape = vintageCursorShape(vintageDate);
    const cursorAnnotation = vintageCursorAnnotation(vintageDate);

    // Fit the y-axis to the visible traces so a single quiet series (e.g.
    // Fixed Income alone) fills the plot instead of hugging a wide band.
    let yMin = Number.POSITIVE_INFINITY;
    let yMax = Number.NEGATIVE_INFINITY;
    for (const t of traces) {
      for (const v of (t as { y?: number[] }).y ?? []) {
        if (v < yMin) yMin = v;
        if (v > yMax) yMax = v;
      }
    }
    const hasData = Number.isFinite(yMin) && Number.isFinite(yMax);
    const pad = hasData ? (yMax - yMin) * 0.1 || 0.25 : 0;
    const yRange: [number, number] | undefined = hasData ? [yMin - pad, yMax + pad] : undefined;

    const preset = compactChartLayout({
      title: 'Expected Returns (1Y horizon, excess, ann.)',
      // Ticks like "0.50%" need more room than the default l:50, or the
      // "% Ann." axis title overlaps them.
      marginOverride: { l: 62 },
    });
    return mergeLayout({
      ...preset,
      xaxis: {
        ...preset.xaxis,
        type: 'date',
        range: [startStr, endPadded.toISOString().slice(0, 10)],
        tickformat,
        ...COMPACT_DATE_TICKS,
        showgrid: true,
        gridcolor: '#E0E0E0',
      },
      yaxis: {
        ...preset.yaxis,
        title: { text: '% Ann.', standoff: 10 },
        ticksuffix: '%',
        tickformat: '.1f',
        showgrid: true,
        gridcolor: '#E0E0E0',
        ...(yRange ? { range: yRange } : {}),
      },
      shapes: cursorShape ? [cursorShape] : [],
      annotations: cursorAnnotation ? [cursorAnnotation] : [],
      hovermode: 'x unified',
    });
  }, [traces, vintageDate, timeframe]);

  return (
    <ChartCard height={300}>
      <QueryState
        query={{ isLoading, isError: false, data: traces }}
        isEmpty={(t) => t.length === 0}
        loadingLabel="Loading"
        emptyLabel="No expected returns in window"
      >
        {() => <PlotlyChart data={traces} layout={layout} />}
      </QueryState>
    </ChartCard>
  );
}
